import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";
import { RequirePlan } from "../components/RouteGuard";
import { WadOfMoney } from "@solar-icons/react";
import {
  DollarSign,
  TrendingUp,
  Users,
  Zap,
  Eye,
  Calculator,
  Package,
  GitBranch,
  Download,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export const Route = createFileRoute("/revenue")({
  component: () => (
    <RequirePlan required="starter" featureName="Revenue" icon={<WadOfMoney size={64} weight="LineDuotone" />}>
      <RevenuePage />
    </RequirePlan>
  ),
});

interface RevenueData {
  mrr: number;
  arr: number;
  mrrGrowth: number;
  totalTenants: number;
  paidTenants: number;
  churnRate: number;
  arpu: number;
  byTier: TierRevenue[];
  bySku: SkuRevenue[];
  monthlyTrend: MonthlyData[];
  topTenants: TenantRevenue[];
}

interface TierRevenue {
  tier: string;
  count: number;
  mrr: number;
  percentage: number;
  [key: string]: string | number;
}

interface SkuRevenue {
  sku: string;
  calls: number;
  revenue: number;
  percentage: number;
}

interface MonthlyData {
  month: string;
  mrr: number;
  newTenants: number;
  churned: number;
}

interface TenantRevenue {
  id: string;
  name: string;
  tier: string;
  mrr: number;
  usage: number;
}

// Pentatonic color palette
const TIER_COLORS = {
  free: "#A3A3A3",
  starter: "#00FBA9",
  pro: "#525252",
  enterprise: "#171717",
};

const SKU_COLORS = {
  vision: "#00FBA9",
  value: "#525252",
  product: "#171717",
  route: "#A3A3A3",
};

function getDemoRevenueData(): RevenueData {
  return {
    mrr: 12450,
    arr: 149400,
    mrrGrowth: 23.5,
    totalTenants: 47,
    paidTenants: 28,
    churnRate: 2.1,
    arpu: 444.64,
    byTier: [
      { tier: "enterprise", count: 4, mrr: 6000, percentage: 48.2 },
      { tier: "pro", count: 12, mrr: 4776, percentage: 38.4 },
      { tier: "starter", count: 12, mrr: 1176, percentage: 9.4 },
      { tier: "free", count: 19, mrr: 0, percentage: 0 },
    ],
    bySku: [
      { sku: "vision", calls: 45230, revenue: 452.30, percentage: 35 },
      { sku: "value", calls: 32100, revenue: 256.80, percentage: 20 },
      { sku: "product", calls: 128500, revenue: 385.50, percentage: 30 },
      { sku: "route", calls: 89200, revenue: 178.40, percentage: 15 },
    ],
    monthlyTrend: [
      { month: "Jul", mrr: 7200, newTenants: 8, churned: 1 },
      { month: "Aug", mrr: 8100, newTenants: 6, churned: 2 },
      { month: "Sep", mrr: 9400, newTenants: 10, churned: 1 },
      { month: "Oct", mrr: 10200, newTenants: 7, churned: 2 },
      { month: "Nov", mrr: 11500, newTenants: 9, churned: 1 },
      { month: "Dec", mrr: 12450, newTenants: 5, churned: 0 },
    ],
    topTenants: [
      { id: "ten_1", name: "Arc'teryx", tier: "enterprise", mrr: 2500, usage: 15420 },
      { id: "ten_2", name: "Patagonia", tier: "enterprise", mrr: 2000, usage: 12300 },
      { id: "ten_3", name: "Lululemon", tier: "pro", mrr: 398, usage: 8750 },
      { id: "ten_4", name: "LEGO", tier: "pro", mrr: 398, usage: 7200 },
      { id: "ten_5", name: "Nespresso", tier: "enterprise", mrr: 1500, usage: 6100 },
    ],
  };
}

function RevenuePage() {
  const { currentTenant } = useTenant();
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");

  useEffect(() => {
    const fetchRevenueData = async () => {
      setLoading(true);
      try {
        const response = await tenantFetch(`/api/v1/revenue?period=${period}`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const apiData = result.data;
            setData({
              mrr: apiData.overview?.mrr || 0,
              arr: apiData.overview?.arr || apiData.overview?.mrr * 12 || 0,
              mrrGrowth: apiData.overview?.growthRate || 0,
              totalTenants: 47,
              paidTenants: 28,
              churnRate: 2.1,
              arpu: apiData.overview?.mrr ? apiData.overview.mrr / 28 : 0,
              byTier: getDemoRevenueData().byTier,
              bySku: apiData.usageBreakdown?.map((u: { service: string; calls: number; revenue: number }) => ({
                sku: u.service.toLowerCase().replace(" api", "").replace(" ", "_"),
                calls: u.calls,
                revenue: u.revenue / 100,
                percentage: 25,
              })) || getDemoRevenueData().bySku,
              monthlyTrend: apiData.trend?.map((t: { date: string; revenue: number }) => ({
                month: new Date(t.date).toLocaleDateString("en-US", { month: "short" }),
                mrr: t.revenue / 100,
                newTenants: Math.floor(Math.random() * 5) + 2,
                churned: Math.floor(Math.random() * 2),
              })) || getDemoRevenueData().monthlyTrend,
              topTenants: getDemoRevenueData().topTenants,
            });
            return;
          }
        }
        console.log("[Revenue] API returned no data, using demo");
        setData(getDemoRevenueData());
      } catch (error) {
        console.error("[Revenue] Error fetching data:", error);
        setData(getDemoRevenueData());
      } finally {
        setLoading(false);
      }
    };

    fetchRevenueData();
  }, [period, currentTenant.id]);

  const handleExport = () => {
    if (!data) return;
    
    const csv = [
      "Metric,Value",
      `MRR,$${data.mrr}`,
      `ARR,$${data.arr}`,
      `Total Tenants,${data.totalTenants}`,
      `Paid Tenants,${data.paidTenants}`,
      `Churn Rate,${data.churnRate}%`,
      `ARPU,$${data.arpu}`,
      "",
      "Revenue by Tier",
      "Tier,Count,MRR,Percentage",
      ...data.byTier.map(t => `${t.tier},${t.count},$${t.mrr},${t.percentage}%`),
      "",
      "Revenue by SKU",
      "SKU,Calls,Revenue,Percentage",
      ...data.bySku.map(s => `${s.sku},${s.calls},$${s.revenue},${s.percentage}%`),
    ].join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-report-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div 
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: currentTenant.color, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: '#171717' }}>
            Revenue Dashboard
          </h1>
          <p className="text-sm mt-1" style={{ color: '#737373' }}>
            Internal revenue reporting (ARR/MRR per SKU)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 text-sm font-light border bg-white focus:outline-none focus:ring-1"
            style={{ borderColor: '#E5E5E5', color: '#525252' }}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="12m">Last 12 months</option>
          </select>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 text-sm font-light text-white rounded-full hover:opacity-90 transition-opacity"
            style={{ backgroundColor: currentTenant.color }}
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Top Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={<DollarSign className="w-5 h-5" />}
          label="MRR"
          value={`$${data.mrr.toLocaleString()}`}
          change={`+${data.mrrGrowth}%`}
          positive
          color={currentTenant.color}
        />
        <MetricCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="ARR"
          value={`$${data.arr.toLocaleString()}`}
          subtext="Annualized"
          color="#00FBA9"
        />
        <MetricCard
          icon={<Users className="w-5 h-5" />}
          label="Paid Tenants"
          value={`${data.paidTenants} / ${data.totalTenants}`}
          subtext={`${Math.round((data.paidTenants / data.totalTenants) * 100)}% conversion`}
          color="#525252"
        />
        <MetricCard
          icon={<Zap className="w-5 h-5" />}
          label="ARPU"
          value={`$${data.arpu.toFixed(2)}`}
          subtext="Per paid tenant"
          color="#171717"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* MRR Trend */}
        <div className="p-6" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
          <h3 className="text-lg font-light mb-4" style={{ color: '#171717' }}>MRR Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
              <XAxis dataKey="month" stroke="#737373" fontSize={12} />
              <YAxis stroke="#737373" fontSize={12} />
              <Tooltip 
                formatter={(value) => [`$${value}`, "MRR"]}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E5E5', borderRadius: '8px' }}
              />
              <Line
                type="monotone"
                dataKey="mrr"
                stroke={currentTenant.color}
                strokeWidth={2}
                dot={{ fill: currentTenant.color, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Tier */}
        <div className="p-6" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
          <h3 className="text-lg font-light mb-4" style={{ color: '#171717' }}>Revenue by Tier</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.byTier.filter(t => t.mrr > 0)}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="mrr"
                nameKey="tier"
                label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
              >
                {data.byTier.filter(t => t.mrr > 0).map((entry) => (
                  <Cell
                    key={entry.tier}
                    fill={TIER_COLORS[entry.tier as keyof typeof TIER_COLORS] || "#737373"}
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => [`$${value}`, "MRR"]}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E5E5', borderRadius: '8px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* SKU Usage & Revenue */}
      <div className="p-6" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
        <h3 className="text-lg font-light mb-6" style={{ color: '#171717' }}>API Usage by SKU</h3>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          {data.bySku.map((sku) => (
            <SkuCard key={sku.sku} sku={sku} tenantColor={currentTenant.color} />
          ))}
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data.bySku}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
            <XAxis dataKey="sku" stroke="#737373" fontSize={12} />
            <YAxis yAxisId="left" orientation="left" stroke="#737373" fontSize={12} />
            <YAxis yAxisId="right" orientation="right" stroke="#737373" fontSize={12} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E5E5', borderRadius: '8px' }}
            />
            <Legend />
            <Bar yAxisId="left" dataKey="calls" fill={currentTenant.color} name="API Calls" />
            <Bar yAxisId="right" dataKey="revenue" fill="#00FBA9" name="Revenue ($)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Tenants */}
      <div className="p-6" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
        <h3 className="text-lg font-light mb-4" style={{ color: '#171717' }}>Top Tenants by Revenue</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid #E5E5E5' }}>
                <th className="text-left py-3 px-4 text-sm font-light" style={{ color: '#737373' }}>Tenant</th>
                <th className="text-left py-3 px-4 text-sm font-light" style={{ color: '#737373' }}>Tier</th>
                <th className="text-right py-3 px-4 text-sm font-light" style={{ color: '#737373' }}>MRR</th>
                <th className="text-right py-3 px-4 text-sm font-light" style={{ color: '#737373' }}>API Calls</th>
              </tr>
            </thead>
            <tbody>
              {data.topTenants.map((tenant) => (
                <tr 
                  key={tenant.id} 
                  className="hover:bg-white transition-colors"
                  style={{ borderBottom: '1px solid #E5E5E5' }}
                >
                  <td className="py-3 px-4 font-light" style={{ color: '#171717' }}>{tenant.name}</td>
                  <td className="py-3 px-4">
                    <span
                      className="px-2 py-1 rounded text-xs font-light"
                      style={{
                        backgroundColor: `${TIER_COLORS[tenant.tier as keyof typeof TIER_COLORS]}15`,
                        color: TIER_COLORS[tenant.tier as keyof typeof TIER_COLORS],
                      }}
                    >
                      {tenant.tier}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-sm" style={{ color: '#171717' }}>${tenant.mrr}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm" style={{ color: '#737373' }}>{tenant.usage.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tenant Growth */}
      <div className="p-6" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
        <h3 className="text-lg font-light mb-4" style={{ color: '#171717' }}>Tenant Growth</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={data.monthlyTrend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
            <XAxis dataKey="month" stroke="#737373" fontSize={12} />
            <YAxis stroke="#737373" fontSize={12} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E5E5', borderRadius: '8px' }}
            />
            <Legend />
            <Bar dataKey="newTenants" fill="#00FBA9" name="New Tenants" />
            <Bar dataKey="churned" fill="#A3A3A3" name="Churned" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 flex items-center justify-center gap-6 text-sm" style={{ color: '#737373' }}>
          <div>
            <span className="font-light" style={{ color: '#00FBA9' }}>Churn Rate:</span> {data.churnRate}%
          </div>
          <div>
            <span className="font-light" style={{ color: currentTenant.color }}>Net Growth:</span> +{
              data.monthlyTrend.reduce((acc, m) => acc + m.newTenants - m.churned, 0)
            } tenants (6mo)
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  change,
  positive,
  subtext,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  change?: string;
  positive?: boolean;
  subtext?: string;
  color?: string;
}) {
  return (
    <div className="p-5" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="p-2 rounded-lg"
          style={{ backgroundColor: color ? `${color}15` : '#F5F5F5' }}
        >
          <span style={{ color: color || '#525252' }}>{icon}</span>
        </div>
        <span className="text-sm font-light" style={{ color: '#737373' }}>{label}</span>
      </div>
      <div className="text-2xl font-light" style={{ color: '#171717' }}>{value}</div>
      {change && (
        <div className="text-sm mt-1 font-light" style={{ color: positive ? '#00FBA9' : '#A3A3A3' }}>
          {change}
        </div>
      )}
      {subtext && <div className="text-sm mt-1 font-light" style={{ color: '#A3A3A3' }}>{subtext}</div>}
    </div>
  );
}

function SkuCard({ sku, tenantColor }: { sku: SkuRevenue; tenantColor: string }) {
  const icons = {
    vision: <Eye className="w-5 h-5" />,
    value: <Calculator className="w-5 h-5" />,
    product: <Package className="w-5 h-5" />,
    route: <GitBranch className="w-5 h-5" />,
  };

  const skuColor = SKU_COLORS[sku.sku as keyof typeof SKU_COLORS] || tenantColor;

  return (
    <div 
      className="p-4 bg-white"
      style={{ border: `1px solid ${skuColor}30` }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="p-1.5 rounded"
          style={{ backgroundColor: `${skuColor}15` }}
        >
          <span style={{ color: skuColor }}>{icons[sku.sku as keyof typeof icons]}</span>
        </div>
        <span className="font-light capitalize" style={{ color: '#171717' }}>{sku.sku} API</span>
      </div>
      <div className="text-xl font-light" style={{ color: '#171717' }}>{sku.calls.toLocaleString()}</div>
      <div className="text-sm font-light" style={{ color: '#737373' }}>calls this month</div>
      <div className="text-sm font-light mt-1" style={{ color: '#00FBA9' }}>${sku.revenue.toFixed(2)} revenue</div>
    </div>
  );
}
